import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Grid,
  TableContainer,
  Button,
  Box
} from '@mui/material';
import {Add, Delete, Visibility} from '@mui/icons-material';
import {Formik} from 'formik';
import {FIND_ALL_SEGMENTOS} from '../../graphql/queries';
import {ButtonComponent, RoundTooltipButton} from '../../components/mui-button';
import InputV2 from '../../components/input-v2/mui-input-v2';
import LottieIcon from '../../components/lottie-icon';
import {LoadingCircle} from '../../assets/lotties';
import TableCellOrdered from '../../components/table-cell-ordered';
import {confirmAlertMui} from '../../components/confirm-alert/mui-confirm-alert';
import {toastNotification} from '../../components/toastify';
import {DELETE_SEGMENTO_ATUACAO} from '../../graphql/mutation';

const columns = [
  {id: 'descricao', label: 'Descrição'},
  {
    id: 'anunciantes',
    label: 'Anunciantes',
    align: 'center',
    calculado: true,
    format: (anunciantes) => anunciantes?.length || 0,
  },
];

const ConsultaSegmentos = ({pageSize = 20}) => {
  const [segmentos, setSegmentos] = useState([]);
  const [rowsPerPage] = useState(pageSize);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState({campo: 'descricao', asc: true});
  const [filtros, setFiltros] = useState({});
  const [searchDTO, setSearchDTO] = useState(filtros);

  const navigate = useNavigate();

  const segmentosQuery = useQuery(FIND_ALL_SEGMENTOS, {
    variables: {
      pageable: {
        pageNumber: page,
        pageSize,
        sortField: orderBy.campo,
        sortDir: orderBy.asc ? 'ASC' : 'DESC',
      },
      searchDTO: {
        search: searchDTO.search || undefined,
      },
    },
  });

  const [deleteSegmento, {loading: loadingDelete}] = useMutation(DELETE_SEGMENTO_ATUACAO);

  useEffect(() => {
    if (segmentosQuery.loading || segmentosQuery.error) return;

    setSegmentos(segmentosQuery.data?.segmentos?.content || []);
  }, [segmentosQuery]);

  const refetch = () => {
    setPage(0);
    setSearchDTO(filtros);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleKeyUp = (event) => {
    if (event.keyCode === 13) {
      refetch();
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);

    return () => window.removeEventListener('keyup', handleKeyUp);

    // eslint-disable-next-line
  }, [filtros]);

  const handleClickDelete = (row) => {
    confirmAlertMui({
      message: 'Deseja mesmo exluir este segmento?',
      onConfirm: () => handleDelete(row),
    });
  };

  const handleDelete = (row) => {
    if (!row.id) return;

    deleteSegmento({
      variables: {
        segmento: {
          id: row.id,
        },
      },
    })
      .then(() => {
        toastNotification({
          message: 'O segmento foi excluído',
          type: 'success',
        });
        segmentosQuery.refetch();
      })
      .catch((error) =>
        toastNotification({message: error.message, type: 'error'}),
      );
  };

  return (
    <Grid id="page-container" container>
      <Button
        startIcon={<Add style={{marginRight: 10}} />}
        id="button-green"
        toolbar="true"
        onClick={() => navigate(`criar`)}
      >
        Cadastrar novo
      </Button>
      <Grid item xs={12}>
        <Formik initialValues={filtros} enableReinitialize>
          <Paper id="filtro-paper">
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <InputV2
                  label="Descrição"
                  value={filtros.search}
                  onChange={({target}) =>
                    setFiltros({...filtros, search: target.value})
                  }
                  className="input-filtro"
                />
              </Grid>
              <Grid item xs={2}>
                <ButtonComponent
                  className="button"
                  value="Filtrar"
                  rightIcon={
                    segmentosQuery.loading && (
                      <LottieIcon icon={LoadingCircle} />
                    )
                  }
                  onClick={() => refetch()}
                  style={{minWidth: 0}}
                />
              </Grid>
            </Grid>
          </Paper>
        </Formik>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Box overflow="auto">
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCellOrdered
                        key={column.id}
                        column={column}
                        orderBy={orderBy}
                        setOrderBy={setOrderBy}
                      />
                    ))}
                    <TableCell
                      key="acoes"
                      align="center"
                      sx={styles.columnHeadAcoes}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {segmentos.map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                        sx={styles.row}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          return (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              onClick={() => navigate(`${row.id}/editar`)}
                            >
                              {column.format ? column.format(value) : value}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <Grid sx={styles.actionButtonsContainer}>
                            <RoundTooltipButton
                              id="icon-cancel"
                              title="Excluir"
                              icon={<Delete />}
                              loading={loadingDelete}
                              onClick={() => handleClickDelete(row)}
                            />
                            <RoundTooltipButton
                              id="icon-primary"
                              title="Visualizar"
                              icon={<Visibility />}
                              onClick={() => navigate(`${row.id}/editar`)}
                            />
                          </Grid>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={segmentosQuery.data?.segmentos?.totalElements || 0}
              rowsPerPageOptions={[]}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                'aria-label': 'página anterior',
              }}
              nextIconButtonProps={{
                'aria-label': 'próxima página',
              }}
              labelDisplayedRows={({from, to, count}) =>
                `${from}-${to} de ${count}`
              }
              onPageChange={handleChangePage}
            />
          </TableContainer>
        </Grid>
      </Grid>
    </Grid>
  );
};

const styles = {
  columnHeadAcoes: {
    cursor: 'default',
    width: '80px',
    fontWeight: 'bold',
  },
  row: {
    cursor: 'pointer',
  },
  actionButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '120px',
    padding: 0,
  },
};

export default ConsultaSegmentos;
